/**
 * Composer file rules + direct-to-storage upload.
 * Limits match the Edge / Widget API presign rules (WIDGET_API.md §3).
 */

import type { PresignedUpload } from '../types/api';
import type { AttachmentKind, ComposerAttachment } from '../types/chat';
import { WidgetApiError } from './errors';

export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** `accept` for the file input — a picker hint only; content is checked below. */
export const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
];

export type UploadExtension = 'jpg' | 'png' | 'webp' | 'pdf';

/**
 * Detects the real type from the first bytes, so a renamed file is refused
 * when it's picked instead of failing on send.
 */
export async function detectFileType(
  file: File,
): Promise<{ extension: UploadExtension; kind: AttachmentKind } | null> {
  const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const ascii = (from: number, to: number) =>
    String.fromCharCode(...head.slice(from, to));

  if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) {
    return { extension: 'jpg', kind: 'image' };
  }
  if (head[0] === 0x89 && ascii(1, 4) === 'PNG') {
    return { extension: 'png', kind: 'image' };
  }
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') {
    return { extension: 'webp', kind: 'image' };
  }
  if (ascii(0, 5) === '%PDF-') {
    return { extension: 'pdf', kind: 'document' };
  }
  return null;
}

/** 1.2 MB / 340 KB — for PDF entries and error messages. */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Send is allowed once every file has uploaded and there's text or at least one file. */
export function canSendWith(
  text: string,
  attachments: ComposerAttachment[],
): boolean {
  if (attachments.some((a) => a.status !== 'done')) return false;
  return text.trim().length > 0 || attachments.length > 0;
}

// Browsers refuse to set these; the request carries them anyway.
const FORBIDDEN_UPLOAD_HEADERS = new Set(['host', 'content-length']);

/**
 * PUT the file to the presigned storage URL. XHR, not fetch, because fetch
 * reports no upload progress. Storage must allow CORS PUT from the panel origin.
 */
export function uploadToStorage(
  presign: PresignedUpload,
  file: File,
  onProgress: (fraction: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(presign.method || 'PUT', presign.upload_url);
    for (const [name, value] of Object.entries(presign.headers)) {
      if (!FORBIDDEN_UPLOAD_HEADERS.has(name.toLowerCase())) {
        xhr.setRequestHeader(name, value);
      }
    }
    xhr.setRequestHeader('Content-Type', presign.content_type);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else
        reject(
          new WidgetApiError(
            'upload_failed',
            `Storage upload failed (${xhr.status})`,
            xhr.status,
          ),
        );
    };
    xhr.onerror = () =>
      reject(new WidgetApiError('upload_failed', 'Storage upload failed', 0));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));

    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}
